import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { AlertCircle, CalendarClock, Handshake, ShieldCheck, Trash2 } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

type CollaborationPermissionLevel = "read_only" | "scoped";
type CollaborationStatus = "pending" | "accepted" | "active" | "revoked" | "expired";

interface CollaborationPermissions {
  modules: string[];
  actions: string[];
}

interface CollaborationSummary {
  id: string;
  tenantId: string;
  tenantName: string | null;
  direction: "incoming" | "outgoing";
  counterpartOrgId: string;
  counterpartOrgName: string | null;
  permissionLevel: CollaborationPermissionLevel;
  permissions: CollaborationPermissions;
  attributionSplit: {
    grantingOrg: number;
    receivingOrg: number;
  } | null;
  status: CollaborationStatus;
  expiresAt: string | null;
  revokedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface OwnedTenantOption {
  id: string;
  name: string;
  slug: string;
}

interface CollaborationPayload {
  collaborations: CollaborationSummary[];
  ownedTenants: OwnedTenantOption[];
}

const MODULE_OPTIONS = [
  { id: "analytics", label: "Analytics" },
  { id: "email", label: "Email" },
  { id: "pages", label: "Pages" },
  { id: "automation", label: "Automation" },
  { id: "crm", label: "CRM" },
  { id: "ads", label: "Ads" },
  { id: "seo", label: "SEO" },
  { id: "social", label: "Social" },
] as const;

const STATUS_STYLES: Record<CollaborationStatus, string> = {
  active: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
  accepted: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  pending: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
  revoked: "bg-[var(--color-error)]/10 text-[var(--color-error)]",
  expired: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
};

function formatDate(value: string | null): string {
  if (!value) return "No expiry";
  return new Date(value).toLocaleDateString();
}

function formatPermissionLabel(value: CollaborationPermissionLevel): string {
  return value === "scoped" ? "Scoped write access" : "Read-only access";
}

function formatSplitLabel(
  split: CollaborationSummary["attributionSplit"],
): string {
  if (!split) return "No attribution split";
  return `${split.grantingOrg}/${split.receivingOrg} attribution`;
}

function CollaborationCard({
  collaboration,
  onRevoke,
  isRevoking,
}: {
  collaboration: CollaborationSummary;
  onRevoke?: (collaborationId: string) => Promise<void>;
  isRevoking: boolean;
}) {
  const canRevoke =
    collaboration.direction === "outgoing" &&
    collaboration.status !== "revoked" &&
    collaboration.status !== "expired";

  return (
    <article className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-[var(--color-foreground)]">
              {collaboration.counterpartOrgName ?? collaboration.counterpartOrgId}
            </p>
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${STATUS_STYLES[collaboration.status]}`}>
              {collaboration.status}
            </span>
          </div>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Tenant: {collaboration.tenantName ?? collaboration.tenantId}
          </p>
          <div className="flex flex-wrap gap-2 text-xs text-[var(--color-muted-foreground)]">
            <span className="rounded-full bg-[var(--color-surface)] px-2.5 py-1">
              {formatPermissionLabel(collaboration.permissionLevel)}
            </span>
            <span className="rounded-full bg-[var(--color-surface)] px-2.5 py-1">
              {formatSplitLabel(collaboration.attributionSplit)}
            </span>
            <span className="rounded-full bg-[var(--color-surface)] px-2.5 py-1">
              Expires {formatDate(collaboration.expiresAt)}
            </span>
          </div>
          {collaboration.permissions.modules.length > 0 && (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Modules: {collaboration.permissions.modules.join(", ")}
            </p>
          )}
          {collaboration.notes && (
            <p className="text-xs text-[var(--color-muted-foreground)]">{collaboration.notes}</p>
          )}
        </div>

        {canRevoke && onRevoke && (
          <button
            type="button"
            disabled={isRevoking}
            onClick={() => onRevoke(collaboration.id)}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-xs font-medium text-[var(--color-error)] hover:bg-[var(--color-error)]/5 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            {isRevoking ? "Revoking..." : "Revoke"}
          </button>
        )}
      </div>
    </article>
  );
}

export default function CollaborationsPage() {
  const { session, isLoading: isAuthLoading } = useAuth();
  const [data, setData] = useState<CollaborationPayload>({
    collaborations: [],
    ownedTenants: [],
  });
  const [collaboratorOrgRef, setCollaboratorOrgRef] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [permissionLevel, setPermissionLevel] =
    useState<CollaborationPermissionLevel>("read_only");
  const [selectedModules, setSelectedModules] = useState<string[]>(["analytics", "email"]);
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [grantingSplit, setGrantingSplit] = useState("60");
  const [receivingSplit, setReceivingSplit] = useState("40");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchCollaborations = useCallback(async () => {
    try {
      const response = await apiFetch<{ data: CollaborationPayload }>(
        "/v1/platform/collaborations",
      );
      setData(response.data);
      if (!tenantId && response.data.ownedTenants[0]) {
        setTenantId(response.data.ownedTenants[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load collaborations");
      setData({ collaborations: [], ownedTenants: [] });
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (isAuthLoading) return;
    if (!session?.activeOrganizationId) {
      setIsLoading(false);
      return;
    }
    void fetchCollaborations();
  }, [fetchCollaborations, isAuthLoading, session?.activeOrganizationId]);

  const outgoingCollaborations = useMemo(
    () => data.collaborations.filter((collaboration) => collaboration.direction === "outgoing"),
    [data.collaborations],
  );
  const incomingCollaborations = useMemo(
    () => data.collaborations.filter((collaboration) => collaboration.direction === "incoming"),
    [data.collaborations],
  );

  const toggleModule = (moduleId: string) => {
    setSelectedModules((current) =>
      current.includes(moduleId)
        ? current.filter((value) => value !== moduleId)
        : [...current, moduleId],
    );
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!tenantId || !collaboratorOrgRef.trim() || selectedModules.length === 0) {
      setError("Tenant, collaborator organization, and at least one module are required.");
      return;
    }

    const granting = Number(grantingSplit);
    const receiving = Number(receivingSplit);
    if (
      Number.isNaN(granting) ||
      Number.isNaN(receiving) ||
      granting < 0 ||
      receiving < 0 ||
      granting + receiving !== 100
    ) {
      setError("Attribution split must sum to 100.");
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await apiFetch<{ data: CollaborationSummary }>("/v1/platform/collaborations", {
        method: "POST",
        body: JSON.stringify({
          collaboratorOrgRef: collaboratorOrgRef.trim(),
          tenantId,
          permissionLevel,
          modules: selectedModules,
          expiresAt: expiresAt ? new Date(`${expiresAt}T00:00:00.000Z`).toISOString() : null,
          notes: notes.trim() || null,
          attributionSplit: {
            grantingOrg: granting,
            receivingOrg: receiving,
          },
        }),
      });
      setSuccess("Collaboration created.");
      setCollaboratorOrgRef("");
      setNotes("");
      setPermissionLevel("read_only");
      setSelectedModules(["analytics", "email"]);
      setExpiresAt("");
      setGrantingSplit("60");
      setReceivingSplit("40");
      await fetchCollaborations();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create collaboration");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevoke = async (collaborationId: string) => {
    const confirmed = window.confirm(
      "Revoke this collaboration now? Access will be removed immediately.",
    );
    if (!confirmed) return;

    setRevokingId(collaborationId);
    setError(null);
    setSuccess(null);
    try {
      await apiFetch<{ data: CollaborationSummary }>(
        `/v1/platform/collaborations/${collaborationId}`,
        { method: "DELETE" },
      );
      setSuccess("Collaboration revoked.");
      await fetchCollaborations();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to revoke collaboration");
    } finally {
      setRevokingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl space-y-4">
        <Skeleton className="h-8 w-56" />
        <SkeletonCard className="h-40" />
        <SkeletonCard className="h-32" />
      </div>
    );
  }

  if (!session?.activeOrganizationId) {
    return (
      <div className="max-w-2xl rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 text-[var(--color-warning)]" />
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">
              Active organization required
            </h2>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
              Collaboration management depends on your active Better Auth organization. Set one active organization and reload this page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8">
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <Handshake className="h-5 w-5 text-[var(--color-primary)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">
            Collaboration access
          </h2>
        </div>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Grant or revoke scoped access for partner agencies from the control plane. Permission level, expiry, and status reflect the live collaboration rows.
        </p>
      </section>

      {error && (
        <div className="rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-3 text-sm text-[var(--color-success)]">
          {success}
        </div>
      )}

      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <div className="mb-5 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[var(--color-primary)]" />
          <h3 className="text-base font-semibold text-[var(--color-foreground)]">
            Grant access
          </h3>
        </div>

        <form onSubmit={handleCreate} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">
                Collaborator organization
              </span>
              <input
                type="text"
                value={collaboratorOrgRef}
                onChange={(event) => setCollaboratorOrgRef(event.target.value)}
                placeholder="Org slug or UUID"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>

            <label className="space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">
                Tenant
              </span>
              <select
                value={tenantId}
                onChange={(event) => setTenantId(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              >
                <option value="">Select tenant</option>
                {data.ownedTenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.slug})
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">
                Permission level
              </span>
              <select
                value={permissionLevel}
                onChange={(event) =>
                  setPermissionLevel(event.target.value as CollaborationPermissionLevel)
                }
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              >
                <option value="read_only">Read-only</option>
                <option value="scoped">Scoped write</option>
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">
                Expiry
              </span>
              <div className="relative">
                <CalendarClock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(event) => setExpiresAt(event.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 pl-9 text-sm text-[var(--color-foreground)]"
                />
              </div>
            </label>
          </div>

          <div className="space-y-2">
            <span className="text-sm font-medium text-[var(--color-foreground)]">
              Allowed modules
            </span>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {MODULE_OPTIONS.map((moduleOption) => {
                const checked = selectedModules.includes(moduleOption.id);
                return (
                  <label
                    key={moduleOption.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                      checked
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-foreground)]"
                        : "border-[var(--color-border)] text-[var(--color-muted-foreground)]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleModule(moduleOption.id)}
                      className="rounded"
                    />
                    {moduleOption.label}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">
                Your attribution %
              </span>
              <input
                type="number"
                min="0"
                max="100"
                value={grantingSplit}
                onChange={(event) => setGrantingSplit(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>

            <label className="space-y-2">
              <span className="text-sm font-medium text-[var(--color-foreground)]">
                Collaborator attribution %
              </span>
              <input
                type="number"
                min="0"
                max="100"
                value={receivingSplit}
                onChange={(event) => setReceivingSplit(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              />
            </label>
          </div>

          <label className="space-y-2">
            <span className="text-sm font-medium text-[var(--color-foreground)]">Notes</span>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
              placeholder="What work is being shared, what should stay off-limits, and how long should the grant live?"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            />
          </label>

          <button
            type="submit"
            disabled={isSaving || data.ownedTenants.length === 0}
            className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSaving ? "Creating..." : "Create collaboration"}
          </button>
        </form>
      </section>

      <section className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-[var(--color-foreground)]">
            Access granted by your organization
          </h3>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            These are the live collaboration rows your active organization controls and can revoke.
          </p>
        </div>
        {outgoingCollaborations.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-muted-foreground)]">
            No outgoing collaborations yet.
          </div>
        ) : (
          <div className="space-y-3">
            {outgoingCollaborations.map((collaboration) => (
              <CollaborationCard
                key={collaboration.id}
                collaboration={collaboration}
                onRevoke={handleRevoke}
                isRevoking={revokingId === collaboration.id}
              />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-[var(--color-foreground)]">
            Access granted to your organization
          </h3>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Incoming collaborations are shown from the same control-plane model for visibility, but revocation stays with the granting org.
          </p>
        </div>
        {incomingCollaborations.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-muted-foreground)]">
            No incoming collaborations.
          </div>
        ) : (
          <div className="space-y-3">
            {incomingCollaborations.map((collaboration) => (
              <CollaborationCard
                key={collaboration.id}
                collaboration={collaboration}
                isRevoking={false}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
